import get from 'lodash.get';
import getConfig from 'next/config';
import {
  GuidesAndLinksData,
  guideCardsCrypto,
  guidesCardsStocks,
} from '../../../constants/partnerPageConst';
import {
  CorporatePage,
  collectionType,
} from '../../../types/partner-page-types';
import { TGuideCard } from '../../Common/GuidesAndUsefullLink';
import { ITR_PLAN_PRODUCT_SERVICE_SUBSTRING } from '../price_cards/utils';
import { IPartnerPlanData } from '../types';

const { publicRuntimeConfig } = getConfig();

export const getCheckoutUrl = (listingId: string): string =>
  `${publicRuntimeConfig.CLEARTAX_DOMAIN}/checkout/${listingId}`;

export const getPlanPageUrl = (planId: string): string =>
  `${publicRuntimeConfig.CLEARTAX_DOMAIN}/services/${planId}/p`;

export const getIsITRPlan = (productServiceType: string): boolean => {
  if (!productServiceType) return false;
  return productServiceType.includes(ITR_PLAN_PRODUCT_SERVICE_SUBSTRING);
};

type TmetaObject = {
  title: string;
  description: string;
  keywords: string;
};
type TPARTNER_META_OBJECT = {
  [key in keyof typeof collectionType]: (
    name: string,
    userType?: string
  ) => TmetaObject;
};

const getDefaultMetaObject = (name: string, userType?: string): TmetaObject => {
  return {
    title: `ITR Filing For ${name}${userType} | Expert Assisted Income Tax Return Filing For ${name}${userType} - ClearTax`,
    description: `ClearTax Offers for ${name}${userType}: File Income Tax Returns (ITR) online by yourself under 3 min and win exciting rewards and discounts on Tax Expert Assisted and CA Tax Filing plans.`,
    keywords: `itr filing ${name}, itr filing for ${name}, itr filing for ${name}${userType}, cleartax filing ${name}, tax filing for ${name}${userType}`,
  };
};
export const getPartnerPageMeta: TPARTNER_META_OBJECT = {
  bfsi: getDefaultMetaObject,
  corporates: getDefaultMetaObject,
  payroll: getDefaultMetaObject,
  crypto: (name) => {
    return {
      title: `Crypto Tax Filing For ${name} Users | Income Tax Return Filing For ${name} Investors Online - ClearTax`,
      description: `${name} Crypto Tax Filing: ITR filing for ${name} users made easy with ClearTax. Upload your ${name} transaction history to calculate crypto tax report and file your Income Tax Return online.`,
      keywords: `${name} crypto tax filing, ${name} tax filing, itr filing for ${name} users in india, ${name} itr filing, income tax filing for ${name} traders and investors, ${name} transaction history, ${name} crypto tax report, cleartax crypto tax filing`,
    };
  },
  stocks: (name) => {
    return {
      title: `ITR Filing For ${name} Users | Income Tax Return Filing For ${name} Traders & Investors Online - ClearTax`,
      description: `${name} Tax Filing: ITR filing for ${name} traders and investors made easy with ClearTax. Import trade wise Tax P&L statement from Equity, Mutual Funds, Intraday, F&O, Currencies or Commodities and file ITR online.`,
      keywords: `${name} tax filing, ${name} itr filing, income tax filing for ${name} traders and investors, ${name} statement for itr, income tax filing ${name}`,
    };
  },
};

export const getMetaTitleDescription = (
  postData: CorporatePage
): {
  seoTitle: string;
  seoDesc: string;
  seoKeywords: string;
} => {
  const postTitle = get(postData, 'seoTitle', '');
  const postDesc = get(postData, 'seoDescription', '');
  const postKeywords = get(postData, 'seoKeywords', '');
  const defaultMetaData = getPartnerPageMeta[postData.collection](
    postData.name,
    getPartnerUserType(postData.headerTitle)
  );
  const title = postTitle || defaultMetaData.title;
  const desc = postDesc || defaultMetaData.description;
  const keywords = postKeywords || defaultMetaData.keywords;

  return { seoTitle: title, seoDesc: desc, seoKeywords: keywords };
};

export const getGuideCardsPerCollection = (
  collection: collectionType
): TGuideCard[] => {
  switch (collection) {
    case 'crypto':
      return guideCardsCrypto;
    case 'stocks':
      return guidesCardsStocks;
    default:
      return GuidesAndLinksData.cards;
  }
};

const getPartnerUserType = (headerTitle: string) => {
  const typeArray = [' User', ' Customer', ' Employee'];
  const typeData = typeArray.find((typeValue) => {
    const regexp = new RegExp(`^.*${typeValue}s?$`, 'i');
    return regexp.test(headerTitle);
  });
  return typeData ? `${typeData}s` : '';
};

export const extractCoupon = (selfITRPlansData: IPartnerPlanData[]) => {
  for (let i = 0; i < selfITRPlansData.length; i++) {
    const element = selfITRPlansData[i];
    if (element?.coupon) {
      return {
        discount: element.coupon.discountValue,
        coupon: element.coupon.code,
      };
    }
  }
  return {
    discount: 0,
    coupon: null,
  };
};
